import { useEffect, useMemo, useState } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
} from 'react-icons/fa';
import logo from '../assets/hodson_logo_3.png';
import styles from '../styles/Footer.module.css';
import { fetchSiteSettings } from '../helpers/contentfulHelper';

const FORM_ENDPOINT = 'https://formspree.io/f/mnnewvzr';

const renderAddress = (addressValue) => {
  if (!addressValue) return null;

  if (typeof addressValue === 'string') {
    const lines = addressValue.split('\n');
    return lines.map((line, index) => (
      <span key={`address-line-${index}`}>
        {line}
        {index < lines.length - 1 ? <br /> : null}
      </span>
    ));
  }

  return documentToReactComponents(addressValue);
};

const Footer = () => {
  const [status, setStatus] = useState('idle');
  const [settingsEntry, setSettingsEntry] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      const data = await fetchSiteSettings();
      setSettingsEntry(data);
    };
    loadSettings();
  }, []);

  const settings = useMemo(() => {
    const fields = settingsEntry?.fields || {};

    const address =
      fields.address ||
      fields.addressLines ||
      fields.companyAddress ||
      null;
    const email = fields.email || fields.contactEmail || null;
    const phoneDisplay = fields.phoneDisplay || fields.phone || null;
    const phoneDial =
      fields.phoneDial ||
      fields.phoneNumber ||
      (phoneDisplay ? String(phoneDisplay).replace(/\s+/g, '') : null);
    const instagramUrl = fields.instagramUrl || null;
    const facebookUrl = fields.facebookUrl || null;
    const twitterUrl = fields.twitterUrl || null;
    const copyrightLine =
      fields.copyrightLine || null;
    const copyrightYear = fields.copyrightYear || null;
    const hasYearInLine = /\b(?:19|20)\d{2}\b/.test(copyrightLine);
    const hasCopyrightSymbolInLine = /©|&copy;/.test(copyrightLine);
    const copyrightPrefix =
      hasYearInLine || hasCopyrightSymbolInLine ? '' : `© ${copyrightYear} `;

    return {
      address,
      email,
      phoneDisplay,
      phoneDial,
      instagramUrl,
      facebookUrl,
      twitterUrl,
      copyrightLine,
      copyrightYear,
      copyrightPrefix,
    };
  }, [settingsEntry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    const formData = new FormData(e.target);

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <div className={styles.infoCols}>
            <div className={styles.col}>
              <h3>Address</h3>
              <address>{renderAddress(settings.address)}</address>
            </div>

            <div className={styles.col}>
              <h3>Connect</h3>
              {settings.email ? (
                <p className={styles.emailRow}>
                  <a href={`mailto:${settings.email}`}>
                    {settings.email}
                  </a>
                </p>
              ) : null}
              {settings.phoneDisplay ? (
                <p>
                  {settings.phoneDial ? (
                    <a href={`tel:${settings.phoneDial}`}>{settings.phoneDisplay}</a>
                  ) : (
                    <span>{settings.phoneDisplay}</span>
                  )}
                </p>
              ) : null}
              <div className={styles.social}>
                {settings.instagramUrl ? (
                  <a
                    href={settings.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </a>
                ) : null}
                {settings.facebookUrl ? (
                  <a
                    href={settings.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <FaFacebookF />
                  </a>
                ) : null}
                {settings.twitterUrl ? (
                  <a
                    href={settings.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Twitter"
                  >
                    <FaTwitter />
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className={styles.formCol}>
            <h3>Do you have a project you would like to discuss?</h3>
            <p className={styles.subhead}>
              Please include as much detail as possible.
            </p>

            <div className={styles.formArea}>
              {status === 'success' ? (
                <p className={styles.success}>
                  Thanks for your message, we&apos;ll be in touch shortly.
                </p>
              ) : (
                <form className={styles.form} onSubmit={handleSubmit}>
                  <input type="text" name="_gotcha" style={{ display: 'none' }} />

                  <div className={styles.row}>
                    <input type="text" name="firstName" placeholder="First name" required />
                    <input type="text" name="surname" placeholder="Surname" required />
                  </div>

                  <div className={styles.row}>
                    <input type="tel" name="telephone" placeholder="Enter your telephone" required />
                    <input type="email" name="email" placeholder="Enter your email" required />
                  </div>

                  <textarea
                    name="message"
                    rows="6"
                    placeholder="What&apos;s your enquiry?"
                    required
                  />

                  <button type="submit" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending…' : 'Submit'}
                  </button>

                  {status === 'error' && (
                    <p className={styles.error}>Something went wrong. Please try again.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>

        <div className={styles.bottom}>
          <div className={styles.wordmarkWrap}>
            <img src={logo} alt="J Hodson & Son" className={styles.footerLogo} />
          </div>
          {settings.copyrightLine ? (
            <div>
              <span>{settings.copyrightPrefix}{settings.copyrightLine}</span>
            </div>
          ) : null}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
